import type { State } from '../State';
import type { AnalyticsEvent } from '../analytics';
import {
  type CardId,
  type CardsInventory,
  type CollectionId,
  cardCollections,
  cardsCatalog,
  cardsCollectionIds,
} from './cards.ruleset';

/** Shared delivery methods that can be recovered by a card-link recipient. */
export type CardsDeliveryMethod = 'request' | 'line_share' | 'native_share';

/** Outbound card-request methods, including the sender-only clipboard result. */
export type CardsRequestMethod = CardsDeliveryMethod | 'copy';

/** Supported origins for opening the Cards Collection V2 UI. */
export type CardsViewOrigin = 'hud' | 'launch' | 'post_level';

/** Recipient state reported when a card-link landing popup opens. */
export type CardsLandingState =
  | 'new_user'
  | 'below_gate'
  | 'has_card'
  | 'missing_card';

/** Supported inbound paths for the canonical CardAccept event. */
export type CardAcceptSource = 'trade' | 'donate' | 'request' | 'invite';

/** Supported terminal reasons for an incomplete card trade. */
export type CardTradeRejectReason = 'rejected' | 'cancelled' | 'expired';

/** Shared collection-progress properties included on active Cards events. */
export type CardsAnalyticsContext = {
  cardUniqueCount: number;
  cardTotalCount: number;
  completeAlbumCount: number;
};

/** Card catalog properties shared by card-specific analytics events. */
export type CardAnalyticsProperties = {
  cardPack: CollectionId;
  cardRarity: number;
  cardTier: 'common' | 'rare' | 'legendary';
};

/** Exact analytics event union member selected by event name. */
type CardsAnalyticsEvent<EventType extends AnalyticsEvent['eventType']> =
  Extract<AnalyticsEvent, { eventType: EventType }>;

/** Request-entry subfeatures keyed by their recoverable delivery method. */
export const cardsRequestSubFeatureByMethod: Record<
  CardsDeliveryMethod,
  string
> = {
  request: 'cards_request',
  line_share: 'cards_request_line_share',
  native_share: 'cards_request_native_share',
};

/** Recover a card-link delivery method from explicit payload attribution. */
export function cardsGetDeliveryMethod(
  method: unknown,
  subFeature: unknown,
): CardsDeliveryMethod {
  // Prefer the explicit method carried by current Card Collection V2 links.
  if (
    method === 'request' ||
    method === 'line_share' ||
    method === 'native_share'
  ) {
    return method;
  }

  // Recover delivery from request subfeatures when explicit data is absent.
  if (subFeature === cardsRequestSubFeatureByMethod.request) return 'request';
  if (subFeature === cardsRequestSubFeatureByMethod.line_share) {
    return 'line_share';
  }

  // Legacy shared links and current URL links both use native-share semantics.
  return 'native_share';
}

/** Copy a user's owned inventory and apply transient count changes. */
export function cardsGetAnalyticsInventory(
  state: State,
  changes: CardsInventory = {},
): CardsInventory {
  // Start from owned cards so analytics never mutate Replicant state.
  const inventory: CardsInventory = { ...(state.cards?.cardsOwned ?? {}) };

  // Apply transient transfers that have not moved from the inbox into ownership.
  for (const [cardId, change] of Object.entries(changes) as [
    CardId,
    number | undefined,
  ][]) {
    if (!change) continue;

    const nextCount = (inventory[cardId] ?? 0) + change;
    if (nextCount <= 0) {
      delete inventory[cardId];
      continue;
    }

    inventory[cardId] = nextCount;
  }

  return inventory;
}

/** Build shared Cards progress context from an inventory snapshot. */
export function cardsGetAnalyticsContextFromInventory(
  inventory: CardsInventory,
): CardsAnalyticsContext {
  // Count only positive inventory entries as unique owned cards.
  const cardUniqueCount = Object.values(inventory).filter(
    (count) => (count ?? 0) > 0,
  ).length;

  // Sum every positive owned count to include duplicates in the total.
  const cardTotalCount = Object.values(inventory).reduce(
    (total, count) => total + Math.max(count ?? 0, 0),
    0,
  );

  // Count sets for which every configured card is present.
  const completeAlbumCount = cardsCollectionIds.filter((collectionId) =>
    cardsIsCollectionComplete(inventory, collectionId),
  ).length;

  return { cardUniqueCount, cardTotalCount, completeAlbumCount };
}

/** Build shared Cards progress context from user state and transient changes. */
export function cardsGetAnalyticsContext(
  state: State,
  changes: CardsInventory = {},
): CardsAnalyticsContext {
  // Derive the effective inventory before calculating collection progress.
  const inventory = cardsGetAnalyticsInventory(state, changes);
  return cardsGetAnalyticsContextFromInventory(inventory);
}

/** Return catalog properties for one valid card ID. */
export function cardsGetCardAnalyticsProperties(
  cardId: CardId,
): CardAnalyticsProperties {
  // Read all card-specific values from the canonical Cards catalog.
  const card = cardsCatalog[cardId];
  return {
    cardPack: card.collection,
    cardRarity: card.rarity,
    cardTier: card.tier,
  };
}

/** Build a CardRequest event for a successfully delivered request. */
export function cardsGetRequestAnalyticsEvent(
  state: State,
  cardId: CardId,
  requestMethod: CardsRequestMethod,
): CardsAnalyticsEvent<'CardRequest'> {
  // Combine request attribution with catalog and current collection context.
  return {
    eventType: 'CardRequest',
    eventProperties: {
      cardID: cardId,
      requestMethod,
      ...cardsGetCardAnalyticsProperties(cardId),
      ...cardsGetAnalyticsContext(state),
    },
  };
}

/** Build a CardSend event from a direct card transfer. */
export function cardsGetSendAnalyticsEvent(
  state: State,
  args: {
    cardId: CardId;
    friendId?: string;
    sendMethod: CardsDeliveryMethod;
    userId?: string;
  },
): CardsAnalyticsEvent<'CardSend'> {
  // Report the sender state after the sent card has left their inventory.
  return {
    userId: args.userId,
    eventType: 'CardSend',
    eventProperties: {
      cardID: args.cardId,
      sendMethod: args.sendMethod,
      ...(args.friendId ? { friendID: args.friendId } : {}),
      ...cardsGetCardAnalyticsProperties(args.cardId),
      ...cardsGetAnalyticsContext(state),
    },
  };
}

/** Build a CardAccept event from a recipient's effective post-receive inventory. */
export function cardsGetAcceptAnalyticsEvent(
  inventory: CardsInventory,
  args: {
    cardId: CardId;
    friendId: string;
    source: CardAcceptSource;
    userId?: string;
  },
): CardsAnalyticsEvent<'CardAccept'> {
  // Report one canonical receive event with final collection context.
  return {
    userId: args.userId,
    eventType: 'CardAccept',
    eventProperties: {
      cardID: args.cardId,
      friendID: args.friendId,
      source: args.source,
      ...cardsGetCardAnalyticsProperties(args.cardId),
      ...cardsGetAnalyticsContextFromInventory(inventory),
    },
  };
}

/**
 * Build CardTradeSend for a direct proposal or a public trade-link share.
 *
 * @param state Proposing player's current Cards state.
 * @param args Trade details, including optional recipient and counter card.
 * @returns Analytics event attributed to the original proposer.
 */
export function cardsGetTradeSendAnalyticsEvent(
  state: State,
  args: {
    tradeId: string;
    offerCardId: CardId;
    requestCardId?: CardId;
    friendId?: string;
    userId?: string;
  },
): CardsAnalyticsEvent<'CardTradeSend'> {
  // Attribute the trade proposal to the original proposing player.
  return {
    userId: args.userId,
    eventType: 'CardTradeSend',
    eventProperties: {
      tradeID: args.tradeId,
      offerCardID: args.offerCardId,
      ...(args.friendId ? { friendID: args.friendId } : {}),
      ...(args.requestCardId ? { requestCardID: args.requestCardId } : {}),
      ...cardsGetAnalyticsContext(state),
    },
  };
}

/** Build CardTradeConfirm for one participant's side of a completed exchange. */
export function cardsGetTradeConfirmAnalyticsEvent(
  inventory: CardsInventory,
  args: {
    tradeId: string;
    offerCardId: CardId;
    receiveCardId: CardId;
    friendId: string;
    userId?: string;
  },
): CardsAnalyticsEvent<'CardTradeConfirm'> {
  // Report the card direction from this event owner's perspective.
  return {
    userId: args.userId,
    eventType: 'CardTradeConfirm',
    eventProperties: {
      tradeID: args.tradeId,
      offerCardID: args.offerCardId,
      receiveCardID: args.receiveCardId,
      friendID: args.friendId,
      ...cardsGetAnalyticsContextFromInventory(inventory),
    },
  };
}

/** Build CardTradeReject for the original proposer after a terminal failure. */
export function cardsGetTradeRejectAnalyticsEvent(
  state: State,
  args: {
    tradeId: string;
    offerCardId: CardId;
    friendId: string;
    reason: CardTradeRejectReason;
    userId?: string;
  },
): CardsAnalyticsEvent<'CardTradeReject'> {
  // Keep terminal trade attribution on the proposer for funnel joins.
  return {
    userId: args.userId,
    eventType: 'CardTradeReject',
    eventProperties: {
      tradeID: args.tradeId,
      offerCardID: args.offerCardId,
      friendID: args.friendId,
      reason: args.reason,
      ...cardsGetAnalyticsContext(state),
    },
  };
}

/** Build CardSetComplete and CardCollectionComplete transition events. */
export function cardsGetCompletionAnalyticsEvents(
  beforeInventory: CardsInventory,
  afterInventory: CardsInventory,
  userId?: string,
): Array<
  | CardsAnalyticsEvent<'CardSetComplete'>
  | CardsAnalyticsEvent<'CardCollectionComplete'>
> {
  // Identify only sets that changed from incomplete to complete.
  const completedSetIds = cardsCollectionIds.filter(
    (collectionId) =>
      !cardsIsCollectionComplete(beforeInventory, collectionId) &&
      cardsIsCollectionComplete(afterInventory, collectionId),
  );
  if (completedSetIds.length === 0) return [];

  // Reuse the final collection context for every transition in this grant.
  const context = cardsGetAnalyticsContextFromInventory(afterInventory);
  const events: Array<
    | CardsAnalyticsEvent<'CardSetComplete'>
    | CardsAnalyticsEvent<'CardCollectionComplete'>
  > = completedSetIds.map((setId) => ({
    userId,
    eventType: 'CardSetComplete',
    eventProperties: {
      setId,
      setsCompleted: context.completeAlbumCount,
      setsIncomplete: cardsCollectionIds.length - context.completeAlbumCount,
      ...context,
    },
  }));

  // Emit the album completion only on the transition to every set complete.
  const wasAlbumComplete = cardsCollectionIds.every((collectionId) =>
    cardsIsCollectionComplete(beforeInventory, collectionId),
  );
  if (
    !wasAlbumComplete &&
    context.completeAlbumCount === cardsCollectionIds.length
  ) {
    events.push({
      userId,
      eventType: 'CardCollectionComplete',
      eventProperties: context,
    });
  }

  return events;
}

/** Return whether every card in one collection exists in an inventory. */
function cardsIsCollectionComplete(
  inventory: CardsInventory,
  collectionId: CollectionId,
): boolean {
  // A collection is complete only when all nine configured cards are owned.
  return cardCollections[collectionId].cards.every(
    (cardId) => (inventory[cardId] ?? 0) > 0,
  );
}
